import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Loader2, Gem } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, assetUrl, errMsg } from "@/lib/api";
import { L } from "@/components/website/DesignTab";
import { ImagePick } from "@/components/website/ContentTab";

const EMPTY = { name: "", category: "", metal: "", weight: "", price: "", image: "", description: "", featured: false };

function ProductForm({ product, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (product) setF({ ...EMPTY, ...product }); }, [product]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = product.id ? await api.put(`/products/${product.id}`, f) : await api.post("/products", f);
      onSaved(data);
      toast.success("Product saved");
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-800" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{product?.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <L label="Photo"><ImagePick value={f.image} onChange={(image) => setF({ ...f, image })} testId="product-image-upload" /></L>
          <L label="Name"><input required className="cf-input h-9 text-sm" value={f.name} onChange={set("name")} placeholder="Bridal Kundan Necklace" data-testid="product-name-input" /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Category"><input className="cf-input h-9 text-sm" list="cats" value={f.category} onChange={set("category")} placeholder="Necklace" data-testid="product-category-input" /></L>
            <L label="Metal / purity"><input className="cf-input h-9 text-sm" value={f.metal} onChange={set("metal")} placeholder="22K Gold 916" data-testid="product-metal-input" /></L>
            <L label="Weight"><input className="cf-input h-9 text-sm" value={f.weight} onChange={set("weight")} placeholder="24.5 g" data-testid="product-weight-input" /></L>
            <L label="Price" hint="Empty = Price on request"><input className="cf-input h-9 text-sm" value={f.price} onChange={set("price")} placeholder="₹1,85,000" data-testid="product-price-input" /></L>
          </div>
          <datalist id="cats">{["Necklace", "Rings", "Earrings", "Bangles", "Bridal", "Diamond", "Silver"].map((c) => <option key={c} value={c} />)}</datalist>
          <L label="Description"><textarea rows={3} className="cf-input text-sm py-2" style={{ height: "auto" }} value={f.description} onChange={set("description")} data-testid="product-description-input" /></L>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="product-featured-checkbox" /> Show first (featured)</label>
          <button disabled={busy} className="cf-btn-primary w-full h-10" data-testid="product-save-button">{busy && <Loader2 size={14} className="animate-spin" />} Save product</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsTab() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/products").then((r) => setList(r.data)); }, []);
  const saved = (d) => setList((l) => (l.some((x) => x.id === d.id) ? l.map((x) => (x.id === d.id ? d : x)) : [d, ...l]));
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/products/${p.id}`); setList((l) => l.filter((x) => x.id !== p.id)); toast.success("Product deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="website-products">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-400">{list ? `${list.length} products in your online collection` : "Loading…"}</p>
        <button className="cf-btn-primary h-9 text-xs px-3" onClick={() => setEditing({})} data-testid="product-add-button"><Plus size={13} /> Add product</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {list?.map((p, i) => (
          <div key={p.id} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid={`product-card-${i}`}>
            <div className="aspect-square bg-slate-800 grid place-items-center relative">
              {p.image ? <img src={assetUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <Gem className="text-slate-600" />}
              {p.featured && <Star size={14} className="absolute top-2 left-2 text-amber-300 fill-amber-300" />}
            </div>
            <div className="p-3">
              <p className="font-semibold text-sm truncate">{p.name}</p>
              <p className="text-[11px] text-slate-500 truncate">{[p.category, p.metal, p.weight].filter(Boolean).join(" · ")}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-amber-300">{p.price || "On request"}</span>
                <span className="flex gap-1">
                  <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white" data-testid={`product-edit-${i}`}><Pencil size={13} /></button>
                  <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" data-testid={`product-delete-${i}`}><Trash2 size={13} /></button>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ProductForm product={editing} onClose={() => setEditing(null)} onSaved={saved} />
    </div>
  );
}
